import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import NavHeader from '@/components/NavHeader';
import { useAuth } from '@pooflabs/web';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeAllLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { subscribeAllDonations } from '@/lib/collections/donations';
import type { DonationsResponse } from '@/lib/collections/donations';
import { BlurFade } from '@/components/effects/BlurFade';
import { Particles } from '@/components/effects/Particles';
import { getTokenByMint, formatTokenAmount } from '@/utils/tokenConfig';
import { SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON, FEE_WALLET } from '@/lib/constants';
import { warmUsdPrices } from '@/utils/usdPricing';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Lock, BarChart2, TrendingUp, Layers, Coins, DollarSign, EyeOff, Wallet, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Nav is handled globally by NavHeader in App.tsx

// ─── Token order ────────────────────────────────────────────────────────────

const TOKEN_ORDER = [SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON];

// Price refresh interval. usdPricing.ts caches quotes for CACHE_TTL_MS (2 min), so
// polling faster than that just serves cached values — this keeps the displayed
// rate from drifting stale forever while staying cheap.
//
// Each sweep staggers its Jupiter quote calls (see warmUsdPrices) instead of firing
// them all at once. At the default ~300ms stagger delay, a full sweep over
// TOKEN_ORDER finishes in well under this interval, so sweeps never overlap — and
// the effect below cancels any in-flight sweep before starting a new one as a
// belt-and-suspenders guard against that.
const PRICE_POLL_INTERVAL_MS = 60_000;

// ─── Custom tooltip for charts ──────────────────────────────────────────────

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-xl"
      style={{
        background: 'hsl(30 6% 9%)',
        border: '1px solid hsl(35 12% 20%)',
        color: 'hsl(40 15% 85%)',
      }}
    >
      {label && <p className="mb-1 font-semibold" style={{ color: 'hsl(38 65% 57%)' }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─── Stat summary card ───────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  accent?: string;
  delay?: number;
}> = ({ label, value, sub, icon, accent = 'hsl(38 65% 57%)', delay = 0 }) => (
  <BlurFade delay={delay}>
    <div
      className="relative overflow-hidden rounded-xl p-6 flex flex-col gap-3 group transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: 'linear-gradient(135deg, hsl(30 6% 7%) 0%, hsl(30 6% 9%) 100%)',
        border: '1px solid hsl(35 12% 14%)',
        boxShadow: `0 0 0 0 ${accent}`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = `${accent}40`;
        (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 32px ${accent}15`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'hsl(35 12% 14%)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 0 transparent';
      }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: `${accent}18`, border: `1px solid ${accent}30`, color: accent }}
      >
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium mb-1" style={{ color: 'hsl(35 8% 52%)' }}>{label}</p>
        <p
          className="text-2xl font-bold tracking-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
        >
          {value}
        </p>
        {sub && <p className="text-xs mt-1" style={{ color: 'hsl(35 8% 45%)' }}>{sub}</p>}
      </div>
    </div>
  </BlurFade>
);

// ─── Chart section wrapper ───────────────────────────────────────────────────

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
}> = ({ title, subtitle, icon, children, delay = 0 }) => (
  <BlurFade delay={delay}>
    <div
      className="rounded-xl p-6 flex flex-col gap-4"
      style={{
        background: 'linear-gradient(135deg, hsl(30 6% 7%) 0%, hsl(30 6% 9%) 100%)',
        border: '1px solid hsl(35 12% 14%)',
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{
            background: 'hsl(38 65% 57% / 0.12)',
            border: '1px solid hsl(38 65% 57% / 0.25)',
            color: 'hsl(38 65% 57%)',
          }}
        >
          {icon}
        </div>
        <div>
          <h3
            className="text-base font-semibold leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs" style={{ color: 'hsl(35 8% 48%)' }}>{subtitle}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  </BlurFade>
);

// ─── Section heading ──────────────────────────────────────────────────────

const SectionHeading: React.FC<{ title: string; subtitle?: string; delay?: number }> = ({ title, subtitle, delay = 0 }) => (
  <BlurFade delay={delay}>
    <div className="mb-4">
      <h2
        className="text-xl font-semibold tracking-tight"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="text-xs mt-1" style={{ color: 'hsl(35 8% 48%)' }}>{subtitle}</p>
      )}
    </div>
  </BlurFade>
);

// ─── Creator-only gate notice — shown to non-creator visitors ──────────────

const CreatorOnlyNotice: React.FC = () => (
  <BlurFade delay={0.1}>
    <div
      className="flex flex-col items-center justify-center py-24 gap-5 rounded-2xl"
      style={{
        background: 'hsl(30 6% 7%)',
        border: '1px solid hsl(35 12% 14%)',
      }}
    >
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{
          background: 'hsl(38 65% 57% / 0.10)',
          border: '1px solid hsl(38 65% 57% / 0.25)',
          color: 'hsl(38 65% 57%)',
        }}
      >
        <EyeOff size={28} />
      </div>
      <div className="text-center max-w-sm px-4">
        <h2
          className="text-2xl font-semibold mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 85%)' }}
        >
          Creator view
        </h2>
        <p className="text-sm" style={{ color: 'hsl(35 8% 48%)' }}>
          Platform-wide statistics are reserved for the project creator and aren't shown here.
        </p>
      </div>
    </div>
  </BlurFade>
);

// ─── Pure aggregation helpers (shared across platform / creator sections) ──

function buildTvlByMint(locks: LocksResponse[]): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const lock of locks) {
    acc[lock.tokenMint] = (acc[lock.tokenMint] ?? 0) + (lock.lkAmt ?? 0);
  }
  return acc;
}

function buildTipsByMint(donations: DonationsResponse[], recipient: string): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const d of donations) {
    if (d.recipient !== recipient) continue;
    acc[d.tokenMint] = (acc[d.tokenMint] ?? 0) + (d.amount ?? 0);
  }
  return acc;
}

function buildUsdByMint(
  amountByMint: Record<string, number>,
  usdPrices: Record<string, number | null>,
): Record<string, number | null> {
  const result: Record<string, number | null> = {};
  for (const mint of TOKEN_ORDER) {
    const rawAmt = amountByMint[mint] ?? 0;
    if (rawAmt === 0) { result[mint] = 0; continue; }
    const price = usdPrices[mint];
    if (price == null) { result[mint] = null; continue; }
    const token = getTokenByMint(mint);
    const humanAmt = rawAmt / Math.pow(10, token.decimals);
    result[mint] = humanAmt * price;
  }
  return result;
}

function computeGrandTotal(usdByMint: Record<string, number | null>): number | null {
  const values = Object.values(usdByMint);
  // Only compute a total if at least one price is available
  if (values.every((v) => v === null || v === 0)) return null;
  return values.reduce<number>((sum, v) => sum + (v ?? 0), 0);
}

// True when a mint with nonzero amount has a null USD price — i.e. the grand total
// above silently excludes that mint's value. Surfaced so the total isn't shown
// as if it were complete when it's actually understated.
function computeIsPartial(
  amountByMint: Record<string, number>,
  usdByMint: Record<string, number | null>,
): boolean {
  return TOKEN_ORDER.some((mint) => (amountByMint[mint] ?? 0) > 0 && usdByMint[mint] == null);
}

function formatUsd(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value === 0) return '$0';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  // Sub-cent values still carry real precision from the underlying quote (not a
  // guess) — show it rather than collapsing to a '<$0.01' placeholder.
  if (value < 0.01) return `$${value.toFixed(8)}`;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Per-token unit price (USD per 1 human unit), always to 8 decimals. Null stays '—'. */
function formatUnitPrice(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value === 0) return '$0';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 })}`;
}

interface TokenCard {
  mint: string;
  symbol: string;
  amount: number;
  color: string;
  formatted: string;
  usdFormatted: string;
}

function buildTokenCards(
  amountByMint: Record<string, number>,
  usdByMint: Record<string, number | null>,
): TokenCard[] {
  return TOKEN_ORDER.filter((mint) => (amountByMint[mint] ?? 0) > 0).map((mint) => {
    const token = getTokenByMint(mint);
    const usdVal = usdByMint[mint];
    return {
      mint,
      symbol: token.symbol,
      amount: amountByMint[mint] ?? 0,
      color: token.brandColor,
      formatted: formatTokenAmount(amountByMint[mint] ?? 0, mint),
      usdFormatted: formatUsd(usdVal),
    };
  });
}

// Lock type palette
const LOCK_TYPE_PALETTE: Record<string, string> = {
  standard: 'hsl(38 65% 57%)',
  perma: 'hsl(168 80% 48%)',
  unknown: 'hsl(35 8% 40%)',
};

// ─── Main stats page ─────────────────────────────────────────────────────────

const StatsPage: React.FC = () => {
  const navigate = useNavigate();

  // Gate: only FEE_WALLET (the creator) sees platform-wide and creator-specific data.
  const { user, loading: authLoading } = useAuth();
  const isCreator = !authLoading && user?.address === FEE_WALLET;

  // USD prices per token (human unit) — only fetched once we know the viewer is the creator.
  const [usdPrices, setUsdPrices] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!isCreator) return;
    let cancelled = false;
    // Tracks the cancel fn for whichever sweep is currently in flight, so a new
    // sweep (interval tick) can stop the previous one before starting — sweeps
    // can never overlap/pile up.
    let cancelCurrentSweep: (() => void) | null = null;

    function runSweep() {
      cancelCurrentSweep?.();
      cancelCurrentSweep = warmUsdPrices(TOKEN_ORDER, (mint, price) => {
        if (cancelled) return;
        setUsdPrices((prev) => ({ ...prev, [mint]: price }));
      });
    }

    runSweep();
    const intervalId = setInterval(runSweep, PRICE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      cancelCurrentSweep?.();
      clearInterval(intervalId);
    };
  }, [isCreator]);

  const { data: allLocks, loading: locksLoading } = useRealtimeData<LocksResponse[]>(
    subscribeAllLocks,
    isCreator,
  );

  const { data: allDonations, loading: donationsLoading } = useRealtimeData<DonationsResponse[]>(
    subscribeAllDonations,
    isCreator,
  );

  const loading = locksLoading || donationsLoading;

  // ── Split active locks into platform (everyone else) vs creator-held ───────

  const activeLocks = useMemo(
    () => (allLocks ?? []).filter((l) => l.status === 'active'),
    [allLocks],
  );

  const platformLocks = useMemo(
    () => activeLocks.filter((l) => l.owner !== FEE_WALLET),
    [activeLocks],
  );

  const creatorLocks = useMemo(
    () => activeLocks.filter((l) => l.owner === FEE_WALLET),
    [activeLocks],
  );

  // TVL per token mint — platform (excludes creator) and creator-held, kept separate.
  const tvlByMint = useMemo(() => buildTvlByMint(platformLocks), [platformLocks]);
  const creatorLockTvlByMint = useMemo(() => buildTvlByMint(creatorLocks), [creatorLocks]);

  // Lifetime tips sent to the creator wallet — a transfer, not a locked balance.
  const tipsByMint = useMemo(
    () => buildTipsByMint(allDonations ?? [], FEE_WALLET),
    [allDonations],
  );

  // Locks over last 30 days bucketed by day — platform only
  const locksOverTime = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const cutoff = now - 30 * 24 * 60 * 60;
    const buckets: Record<string, number> = {};

    // Pre-fill all 30 days
    for (let d = 0; d < 30; d++) {
      const dayTs = now - (29 - d) * 24 * 60 * 60;
      const label = new Date(dayTs * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      buckets[label] = 0;
    }

    for (const lock of platformLocks) {
      if (lock.ts >= cutoff) {
        const label = new Date(lock.ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (label in buckets) {
          buckets[label] = (buckets[label] ?? 0) + 1;
        }
      }
    }

    return Object.entries(buckets).map(([date, count]) => ({ date, count }));
  }, [platformLocks]);

  // Lock type distribution — platform only
  const lockTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const lock of platformLocks) {
      const t = lock.lockType ?? 'unknown';
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [platformLocks]);

  // Token distribution (count per mint) — platform only
  const tokenDistData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const lock of platformLocks) {
      counts[lock.tokenMint] = (counts[lock.tokenMint] ?? 0) + 1;
    }
    return TOKEN_ORDER
      .filter((mint) => (counts[mint] ?? 0) > 0)
      .map((mint) => ({
        name: getTokenByMint(mint).symbol,
        value: counts[mint] ?? 0,
        color: getTokenByMint(mint).brandColor,
        mint,
      }));
  }, [platformLocks]);

  const platformEmpty = platformLocks.length === 0 && !loading;

  // ── USD values per token — platform, creator-locked, and tips kept separate ─

  const usdByMint = useMemo(() => buildUsdByMint(tvlByMint, usdPrices), [tvlByMint, usdPrices]);
  const creatorLockUsdByMint = useMemo(
    () => buildUsdByMint(creatorLockTvlByMint, usdPrices),
    [creatorLockTvlByMint, usdPrices],
  );
  const tipsUsdByMint = useMemo(() => buildUsdByMint(tipsByMint, usdPrices), [tipsByMint, usdPrices]);

  const grandTotalUsd = useMemo(() => computeGrandTotal(usdByMint), [usdByMint]);
  const grandTotalIsPartial = useMemo(
    () => computeIsPartial(tvlByMint, usdByMint),
    [tvlByMint, usdByMint],
  );

  const creatorLockGrandTotalUsd = useMemo(() => computeGrandTotal(creatorLockUsdByMint), [creatorLockUsdByMint]);
  const creatorLockGrandTotalIsPartial = useMemo(
    () => computeIsPartial(creatorLockTvlByMint, creatorLockUsdByMint),
    [creatorLockTvlByMint, creatorLockUsdByMint],
  );

  const tipsGrandTotalUsd = useMemo(() => computeGrandTotal(tipsUsdByMint), [tipsUsdByMint]);
  const tipsGrandTotalIsPartial = useMemo(
    () => computeIsPartial(tipsByMint, tipsUsdByMint),
    [tipsByMint, tipsUsdByMint],
  );

  // ── Per-token cards ──────────────────────────────────────────────────────

  const tvlCards = useMemo(() => buildTokenCards(tvlByMint, usdByMint), [tvlByMint, usdByMint]);
  const creatorLockCards = useMemo(
    () => buildTokenCards(creatorLockTvlByMint, creatorLockUsdByMint),
    [creatorLockTvlByMint, creatorLockUsdByMint],
  );
  const tipsCards = useMemo(() => buildTokenCards(tipsByMint, tipsUsdByMint), [tipsByMint, tipsUsdByMint]);

  return (
    <PageLayout footer>
      <NavHeader />
      {/* bg particles */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <Particles
          quantity={30}
          color="hsl(38 65% 57%)"
          size={0.4}
          className="opacity-30"
        />
      </div>

      <div className="relative pt-24 pb-16" style={{ zIndex: 1 }}>
        <div className="container max-w-6xl mx-auto px-4">

          {/* ── Page header ─────────────────────────────────────────────── */}
          <BlurFade delay={0}>
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'hsl(38 65% 57% / 0.12)',
                    border: '1px solid hsl(38 65% 57% / 0.3)',
                    color: 'hsl(38 65% 57%)',
                  }}
                >
                  <BarChart2 size={20} />
                </div>
                <h1
                  className="text-4xl font-semibold tracking-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
                >
                  Protocol Statistics
                </h1>
              </div>
              <p className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>
                Real-time on-chain data for active SelfWorth escrow positions.
              </p>
            </div>
          </BlurFade>

          {/* ── Resolving wallet ─────────────────────────────────────────── */}
          {authLoading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div
                className="w-12 h-12 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'hsl(38 65% 57% / 0.2)',
                  borderTopColor: 'hsl(38 65% 57%)',
                }}
              />
              <p className="text-sm" style={{ color: 'hsl(35 8% 45%)' }}>
                Checking wallet…
              </p>
            </div>
          )}

          {/* ── Non-creator visitors ─────────────────────────────────────── */}
          {!authLoading && !isCreator && <CreatorOnlyNotice />}

          {/* ── Creator view ──────────────────────────────────────────────── */}
          {!authLoading && isCreator && (
            <>
              {loading && (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div
                    className="w-12 h-12 rounded-full border-2 animate-spin"
                    style={{
                      borderColor: 'hsl(38 65% 57% / 0.2)',
                      borderTopColor: 'hsl(38 65% 57%)',
                    }}
                  />
                  <p className="text-sm" style={{ color: 'hsl(35 8% 45%)' }}>
                    Loading protocol data…
                  </p>
                </div>
              )}

              {!loading && (
                <>
                  {/* ══════════════════ Creator wallet section ══════════════════ */}
                  <SectionHeading
                    title="Creator Wallet"
                    subtitle="Funds tied to the FEE_WALLET address — kept separate from the platform totals below. Locked balances and lifetime tips are different kinds of funds and are never combined."
                    delay={0.02}
                  />

                  {/* Creator locked */}
                  <div className="mb-6">
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'hsl(35 8% 52%)' }}
                    >
                      Creator Locked
                    </p>
                    {creatorLockCards.length === 0 ? (
                      <p className="text-xs" style={{ color: 'hsl(35 8% 40%)' }}>
                        No active creator-held locks.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {creatorLockGrandTotalUsd != null && (
                          <SummaryCard
                            label="Creator Locked · Total"
                            value={
                              creatorLockGrandTotalIsPartial
                                ? `~${formatUsd(creatorLockGrandTotalUsd)}`
                                : formatUsd(creatorLockGrandTotalUsd)
                            }
                            sub={creatorLockGrandTotalIsPartial ? 'approximate — some prices unavailable' : 'across all tokens'}
                            icon={<Wallet size={18} />}
                            accent="hsl(145 60% 55%)"
                            delay={0.04}
                          />
                        )}
                        {creatorLockCards.map((t, i) => (
                          <SummaryCard
                            key={`cl-${t.mint}`}
                            label={`Locked · ${t.symbol}`}
                            value={t.formatted}
                            sub={t.usdFormatted !== '—' && t.usdFormatted !== '$0' ? `≈ ${t.usdFormatted}` : 'total locked'}
                            icon={<Lock size={18} />}
                            accent={t.color}
                            delay={0.06 + i * 0.03}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tips received */}
                  <div className="mb-10">
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'hsl(35 8% 52%)' }}
                    >
                      Tips Received (Lifetime)
                    </p>
                    {tipsCards.length === 0 ? (
                      <p className="text-xs" style={{ color: 'hsl(35 8% 40%)' }}>
                        No tips received yet.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {tipsGrandTotalUsd != null && (
                          <SummaryCard
                            label="Tips Received · Total"
                            value={
                              tipsGrandTotalIsPartial
                                ? `~${formatUsd(tipsGrandTotalUsd)}`
                                : formatUsd(tipsGrandTotalUsd)
                            }
                            sub={tipsGrandTotalIsPartial ? 'approximate — some prices unavailable' : 'lifetime, all tokens'}
                            icon={<Gift size={18} />}
                            accent="hsl(38 65% 57%)"
                            delay={0.04}
                          />
                        )}
                        {tipsCards.map((t, i) => (
                          <SummaryCard
                            key={`tip-${t.mint}`}
                            label={`Tips · ${t.symbol}`}
                            value={t.formatted}
                            sub={t.usdFormatted !== '—' && t.usdFormatted !== '$0' ? `≈ ${t.usdFormatted}` : 'lifetime total'}
                            icon={<Gift size={18} />}
                            accent={t.color}
                            delay={0.06 + i * 0.03}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Live per-token unit prices — reuses usdPrices state, no extra fetch/poll */}
                  <div className="mb-10">
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'hsl(35 8% 52%)' }}
                    >
                      Current Token Prices
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {TOKEN_ORDER.map((mint, i) => {
                        const token = getTokenByMint(mint);
                        return (
                          <SummaryCard
                            key={`price-${mint}`}
                            label={`${token.symbol} / unit`}
                            value={formatUnitPrice(usdPrices[mint])}
                            sub={token.name}
                            icon={<DollarSign size={18} />}
                            accent={token.brandColor}
                            delay={0.04 + i * 0.03}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* ══════════════════ Platform section ══════════════════ */}
                  <SectionHeading
                    title="Platform Totals"
                    subtitle="Excludes locks and funds held by the creator wallet above."
                    delay={0.1}
                  />

                  {platformEmpty ? (
                    <BlurFade delay={0.12}>
                      <div
                        className="flex flex-col items-center justify-center py-24 gap-5 rounded-2xl"
                        style={{
                          background: 'hsl(30 6% 7%)',
                          border: '1px solid hsl(35 12% 14%)',
                        }}
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{
                            background: 'hsl(38 65% 57% / 0.10)',
                            border: '1px solid hsl(38 65% 57% / 0.25)',
                            color: 'hsl(38 65% 57%)',
                          }}
                        >
                          <Lock size={28} />
                        </div>
                        <div className="text-center">
                          <h2
                            className="text-2xl font-semibold mb-2"
                            style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 85%)' }}
                          >
                            No active locks yet
                          </h2>
                          <p className="text-sm" style={{ color: 'hsl(35 8% 48%)' }}>
                            Be the first to lock tokens and grow the protocol.
                          </p>
                        </div>
                        <Button
                          onClick={() => navigate('/dashboard')}
                          style={{
                            background: 'hsl(38 65% 57% / 0.15)',
                            border: '1px solid hsl(38 65% 57% / 0.4)',
                            color: 'hsl(38 65% 57%)',
                          }}
                        >
                          <Lock size={14} className="mr-2" />
                          Create a Lock
                        </Button>
                      </div>
                    </BlurFade>
                  ) : (
                    <>
                      {/* ── Top summary stats ─────────────────────────────────── */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                        <SummaryCard
                          label="Active Locks"
                          value={platformLocks.length.toLocaleString()}
                          sub="open escrow positions"
                          icon={<Lock size={18} />}
                          accent="hsl(38 65% 57%)"
                          delay={0.05}
                        />
                        {grandTotalUsd != null && (
                          <SummaryCard
                            label="Total USD Locked"
                            value={grandTotalIsPartial ? `~${formatUsd(grandTotalUsd)}` : formatUsd(grandTotalUsd)}
                            sub={grandTotalIsPartial ? 'approximate — some prices unavailable' : 'across all tokens'}
                            icon={<DollarSign size={18} />}
                            accent="hsl(145 60% 55%)"
                            delay={0.07}
                          />
                        )}
                        {tvlCards.map((t, i) => (
                          <SummaryCard
                            key={t.mint}
                            label={`TVL · ${t.symbol}`}
                            value={t.formatted}
                            sub={t.usdFormatted !== '—' && t.usdFormatted !== '$0' ? `≈ ${t.usdFormatted}` : 'total locked'}
                            icon={<Coins size={18} />}
                            accent={t.color}
                            delay={0.09 + i * 0.04}
                          />
                        ))}
                      </div>

                      {/* ── Charts grid ───────────────────────────────────────── */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Chart 1: Locks over time */}
                        <ChartCard
                          title="Locks Created (Last 30 Days)"
                          subtitle="Active escrow positions opened per day"
                          icon={<TrendingUp size={15} />}
                          delay={0.15}
                        >
                          <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={locksOverTime} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="hsl(38 65% 57%)" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="hsl(38 65% 57%)" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis
                                dataKey="date"
                                tick={{ fill: 'hsl(35 8% 40%)', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                interval={6}
                              />
                              <YAxis
                                tick={{ fill: 'hsl(35 8% 40%)', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                              />
                              <Tooltip content={<ChartTooltip />} />
                              <Area
                                type="monotone"
                                dataKey="count"
                                name="Locks"
                                stroke="hsl(38 65% 57%)"
                                strokeWidth={2}
                                fill="url(#goldGrad)"
                                dot={false}
                                activeDot={{ r: 4, fill: 'hsl(43 75% 68%)' }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </ChartCard>

                        {/* Chart 2: Lock type distribution */}
                        <ChartCard
                          title="Lock Type Distribution"
                          subtitle="Standard vs. PermaLOCK positions"
                          icon={<Layers size={15} />}
                          delay={0.2}
                        >
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={lockTypeData}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={3}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) =>
                                  `${name.charAt(0).toUpperCase() + name.slice(1)} ${(percent * 100).toFixed(0)}%`
                                }
                                labelLine={false}
                              >
                                {lockTypeData.map((entry, idx) => (
                                  <Cell
                                    key={`lt-${idx}`}
                                    fill={LOCK_TYPE_PALETTE[entry.name] ?? 'hsl(35 8% 40%)'}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const p = payload[0];
                                  return (
                                    <div
                                      className="rounded-lg px-3 py-2 text-xs shadow-xl"
                                      style={{
                                        background: 'hsl(30 6% 9%)',
                                        border: '1px solid hsl(35 12% 20%)',
                                        color: 'hsl(40 15% 85%)',
                                      }}
                                    >
                                      <p style={{ color: LOCK_TYPE_PALETTE[p.name as string] ?? 'hsl(38 65% 57%)' }}>
                                        {String(p.name).charAt(0).toUpperCase() + String(p.name).slice(1)}:{' '}
                                        <strong>{p.value as number} locks</strong>
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                              <Legend
                                formatter={(value) =>
                                  <span style={{ color: 'hsl(35 8% 60%)', fontSize: 11 }}>
                                    {String(value).charAt(0).toUpperCase() + String(value).slice(1)}
                                  </span>
                                }
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </ChartCard>

                        {/* Chart 3: Token distribution */}
                        <ChartCard
                          title="Active Locks by Token"
                          subtitle="Count of open positions per asset"
                          icon={<Coins size={15} />}
                          delay={0.25}
                        >
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              data={tokenDistData}
                              margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                            >
                              <XAxis
                                dataKey="name"
                                tick={{ fill: 'hsl(35 8% 52%)', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fill: 'hsl(35 8% 40%)', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const p = payload[0];
                                  const item = tokenDistData.find((d) => d.name === p.name ||
                                    (payload[0] as { payload?: { name: string } })?.payload?.name === d.name);
                                  return (
                                    <div
                                      className="rounded-lg px-3 py-2 text-xs shadow-xl"
                                      style={{
                                        background: 'hsl(30 6% 9%)',
                                        border: '1px solid hsl(35 12% 20%)',
                                        color: 'hsl(40 15% 85%)',
                                      }}
                                    >
                                      <p style={{ color: item?.color ?? 'hsl(38 65% 57%)' }}>
                                        {item?.name}: <strong>{p.value as number} locks</strong>
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="value" name="Locks" radius={[4, 4, 0, 0]}>
                                {tokenDistData.map((entry, idx) => (
                                  <Cell key={`td-${idx}`} fill={entry.color} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartCard>

                        {/* Token TVL breakdown bar chart */}
                        <ChartCard
                          title="Value Locked by Token"
                          subtitle="Human-readable amounts per asset (log scale)"
                          icon={<TrendingUp size={15} />}
                          delay={0.3}
                        >
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              data={TOKEN_ORDER
                                .filter((mint) => (tvlByMint[mint] ?? 0) > 0)
                                .map((mint) => {
                                  const token = getTokenByMint(mint);
                                  const raw = tvlByMint[mint] ?? 0;
                                  const divisor = Math.pow(10, token.decimals);
                                  return {
                                    name: token.symbol,
                                    value: parseFloat((raw / divisor).toFixed(token.decimals <= 5 ? 0 : 4)),
                                    color: token.brandColor,
                                  };
                                })}
                              margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                            >
                              <XAxis
                                dataKey="name"
                                tick={{ fill: 'hsl(35 8% 52%)', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fill: 'hsl(35 8% 40%)', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                scale="log"
                                domain={['auto', 'auto']}
                                allowDataOverflow
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const entry = (payload[0] as { payload?: { name: string; value: number; color: string } })?.payload;
                                  return (
                                    <div
                                      className="rounded-lg px-3 py-2 text-xs shadow-xl"
                                      style={{
                                        background: 'hsl(30 6% 9%)',
                                        border: '1px solid hsl(35 12% 20%)',
                                        color: 'hsl(40 15% 85%)',
                                      }}
                                    >
                                      <p style={{ color: entry?.color ?? 'hsl(38 65% 57%)' }}>
                                        {entry?.name}: <strong>{entry?.value?.toLocaleString()}</strong>
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="value" name="Amount" radius={[4, 4, 0, 0]}>
                                {TOKEN_ORDER
                                  .filter((mint) => (tvlByMint[mint] ?? 0) > 0)
                                  .map((mint, idx) => (
                                    <Cell key={`tvl-${idx}`} fill={getTokenByMint(mint).brandColor} />
                                  ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartCard>
                      </div>
                    </>
                  )}

                  {/* ── Footer note ──────────────────────────────────────── */}
                  <BlurFade delay={0.35}>
                    <p
                      className="text-center text-xs mt-10"
                      style={{ color: 'hsl(35 8% 38%)' }}
                    >
                      All data is sourced directly from on-chain escrow state. Refreshed in real-time.
                    </p>
                  </BlurFade>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default StatsPage;
